import { useDebounce, useI18N } from "@sundae/react-hooks";
import {
  Balance,
  ChevronDownIcon,
  Collapsible,
  FlexList,
  FlexListItem,
  Loader,
  Percentage,
  Text,
  toolkitCx,
} from "@sundae/ui-toolkit";
import { AssetAmount } from "@sundaeswap/asset";
import cx from "classnames";
import { FC, useCallback, useEffect, useState } from "react";

import { ADA_TX_FEE } from "../../../constants/SundaeSwap.constants";
import { ADA_METADATA } from "../../../constants/cardano.constants";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { useTasteTestTx } from "../../../pages/TasteTestDetail/hooks/useTasteTestTx";
import { useProfileAppContext } from "../../../stores";
import { EOrderTransactionBuilderState, useOrderContext } from "../../../stores/OrderContext";
import { IAssetMetaData } from "../../../types/Asset.types";
import { getAssetName } from "../../../utils/assets.utils";
import { formatCurrencyToAda } from "../../../utils/number-format";
import AssetWithAssetName from "../../AssetWithAssetName";
import BalanceWithAssetName from "../../BalanceWithAssetName";

const TasteTestDepositTransactionDetails: FC = () => {
  const { currentLocale, t } = useI18N("taste-test");
  const [open, setOpen] = useState(false);

  const {
    derived: {
      contributionOutcome,
      depositAssetAmount,
      depositAssetAmountExceedsBalance,
      hasContribution,
    },
    state: { actionType, debouncedDepositValue, tasteTest },
  } = useTasteTestContext();
  const {
    state: {
      derived: { transaction },
    },
  } = useOrderContext();
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();
  const debouncedDepositAssetAmountExceedsBalance = useDebounce(depositAssetAmountExceedsBalance, {
    wait: 500,
  });
  const { buildTx } = useTasteTestTx({ type: actionType });
  const givenMetadata = depositAssetAmount?.metadata ?? ({} as IAssetMetaData);
  const takenMetadata = (tasteTest?.asset as unknown as IAssetMetaData) ?? ({} as IAssetMetaData); // using the tt asset here since `taken` metadata might not always be available

  const foldFee = new AssetAmount(tasteTest?.parameters?.foldFee ?? "0", ADA_METADATA);
  const minUTXO = new AssetAmount(tasteTest?.parameters?.minUTXO ?? "0", ADA_METADATA);
  const handleCollapsible = useCallback(() => setOpen((prev) => !prev), [setOpen]);

  const transactionSummary = [
    {
      id: "1",
      label: t("pool", { ns: "glossary" }),
      value: (
        <AssetWithAssetName
          assetName={`${getAssetName(givenMetadata)}-${getAssetName(takenMetadata)}`}
          assets={[givenMetadata, takenMetadata]}
        />
      ),
    },
    {
      id: "2",
      className: "!items-start",
      label: t("detail.txOverview.lpTokensValue"),
      value: contributionOutcome && (
        <Text tag="div" className="inline-flex gap-1" size="sm">
          <Balance
            abbreviate={false}
            size="sm"
            locale={currentLocale}
            privacy={privacyEnabled}
            value={contributionOutcome.contributionValueAda}
          />{" "}
          /{" "}
          <BalanceWithAssetName
            abbreviate={false}
            className="inline-block"
            asset={contributionOutcome.contributionValueToken.metadata as unknown as IAssetMetaData}
            size="sm"
            showSymbol={false}
            locale={currentLocale}
            privacy={privacyEnabled}
            value={contributionOutcome.contributionValueToken}
          />
        </Text>
      ),
    },
    {
      id: "3",
      label: t("preview.price"),
      value: contributionOutcome && (
        <div className="flex items-center justify-end gap-1">
          <Text size="sm" tag="span">
            {formatCurrencyToAda(contributionOutcome.assetRatio.getLimitPrice(), currentLocale)}
          </Text>
          <Text size="sm" tag="span">
            {t("per", {
              ns: "liquidity",
              firstAsset: undefined,
              secondAsset: getAssetName(takenMetadata),
            })}
          </Text>
        </div>
      ),
    },
    {
      id: "4",
      label: t("share", { ns: "common", context: "poolEst" }),
      value: (
        <Percentage
          abbreviate
          locale={currentLocale}
          size="sm"
          value={contributionOutcome?.poolShare ?? 0}
        />
      ),
    },
  ];

  useEffect(() => {
    if (
      debouncedDepositValue &&
      debouncedDepositValue !== "0" &&
      !debouncedDepositAssetAmountExceedsBalance
    ) {
      buildTx(); // TODO: listen to input change
    }
  }, [debouncedDepositValue, debouncedDepositAssetAmountExceedsBalance]);

  return (
    <Collapsible
      className="motion-safe:animate-slide-up"
      open={open}
      trigger={
        <div className="flex flex-col gap-2">
          {/* -------------------------------------------------------------------------------------------------
           * ADA Transaction Fee, Fold Fee and Deposit
           * -----------------------------------------------------------------------------------------------*/}
          <FlexList>
            <FlexListItem
              field={{
                label: (
                  <Text size="sm" tag="span" variant="muted">
                    {t("fee", { context: "adaTx", ns: "fees" })}
                  </Text>
                ),
                variant: "muted",
              }}
              tooltip={t("fee", {
                context: transaction?.completeTx ? "adaTxExplained" : "adaTxExplainedNextStep",
                ns: "fees",
              })}
              value={{
                label:
                  transaction?.state === EOrderTransactionBuilderState.BUILD ? (
                    <Loader className="mr-2" size="xsmall" />
                  ) : (
                    <div className="inline-flex gap-[2px]">
                      {!transaction?.builtTx?.fees?.cardanoTxFee && (
                        <Text tag="span" size="sm">
                          ~{" "}
                        </Text>
                      )}
                      <Balance
                        abbreviate={false}
                        locale={currentLocale}
                        showSymbol
                        size="sm"
                        value={transaction?.builtTx?.fees?.cardanoTxFee || ADA_TX_FEE}
                      />
                    </div>
                  ),
              }}
            />
            {!hasContribution && (
              <>
                <FlexListItem
                  field={{
                    label: t("detail.widget.processorFee"),
                    size: "sm",
                    variant: "muted",
                  }}
                  tooltip={t("detail.widget.processorFee", { context: "tooltip" })}
                  value={{
                    label:
                      transaction?.state === EOrderTransactionBuilderState.BUILD ? (
                        <Loader className="mr-2" size="xsmall" />
                      ) : (
                        <Balance
                          abbreviate={false}
                          locale={currentLocale}
                          showSymbol
                          size="sm"
                          value={foldFee}
                        />
                      ),
                  }}
                />
                <FlexListItem
                  field={{
                    label: t("detail.widget.refundableDeposit"),
                    size: "sm",
                    variant: "muted",
                  }}
                  tooltip={t("fee", { context: "depositExplained", ns: "fees" })}
                  value={{
                    label:
                      transaction?.state === EOrderTransactionBuilderState.BUILD ? (
                        <Loader className="mr-2" size="xsmall" />
                      ) : (
                        <Balance
                          abbreviate={false}
                          locale={currentLocale}
                          showSymbol
                          size="sm"
                          value={minUTXO}
                        />
                      ),
                  }}
                />
              </>
            )}
          </FlexList>

          <Text
            role="button"
            className={cx(
              "-ml-4 -mr-4 w-[calc(100%+2rem)] cursor-pointer rounded-lg py-1.5",
              toolkitCx.layout.spacing.horizontal.md,

              {
                [toolkitCx.layout.background.card]: !open,
                "bg-primary:20 dark:bg-primary/10": open,
              }
            )}
            tag="div"
            size="sm"
            variant="muted"
            onClick={handleCollapsible}
          >
            {t(`detail.widget.deposit.orderPreview.details.${open ? "hide" : "show"}`)}{" "}
            <ChevronDownIcon
              className={cx(
                "inline h-4 w-4",
                {
                  "-scale-y-100": open,
                },
                toolkitCx.layout.text.muted,
                toolkitCx.layout.transition.transform
              )}
            />
          </Text>
        </div>
      }
    >
      <FlexList className="mt-4">
        {transactionSummary.map(({ className, id, label, value }) => (
          <FlexListItem
            key={id}
            className={className}
            field={{ label, variant: "muted" }}
            value={{
              label: value,
            }}
          />
        ))}
      </FlexList>
    </Collapsible>
  );
};

export default TasteTestDepositTransactionDetails;
